import { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, NubeskIcons } from "components/atoms";
import { useTask } from "_hooks";
import {
  ITEM_DONE,
  ITEM_DECLINED,
  ITEM_INPROGRESS,
  ITEM_PENDING,
  ITEM_TOREVIEW
} from "_constants/task.constants";

const prefix = "m-task-item-status-buttons";

const statusClassMap = {
  [ITEM_PENDING]: "u-bg-gray",
  [ITEM_INPROGRESS]: "u-bg-blue",
  [ITEM_TOREVIEW]: "u-bg-yellow",
  [ITEM_DONE]: "u-bg-light-green",
  [ITEM_DECLINED]: "u-bg-red"
};

const statusLabelClass = {
  [ITEM_PENDING]: "u-purple",
  [ITEM_INPROGRESS]: "u-white",
  [ITEM_TOREVIEW]: "u-dark-gray",
  [ITEM_DONE]: "u-white",
  [ITEM_DECLINED]: "u-white"
};

const statusFlow = [
  ITEM_PENDING,
  ITEM_INPROGRESS,
  ITEM_TOREVIEW,
  ITEM_DONE
];

const TaskItemStatusButtons = ({ taskKey, productId, currentStatus, refreshKey = "taskItems" }) => {
  const { t } = useTranslation();
  const { setProcessStatus } = useTask();

  const { canGoBack, canGoNext, previousStatus, nextStatus } = useMemo(() => {
    const currentIndex = statusFlow.indexOf(currentStatus);

    const result = {
      canGoBack: currentIndex > 0,
      canGoNext: currentIndex >= 0 && currentIndex < statusFlow.length - 1,
      previousStatus: currentIndex > 0 ? statusFlow[currentIndex - 1] : null,
      nextStatus: currentIndex >= 0 && currentIndex < statusFlow.length - 1 ? statusFlow[currentIndex + 1] : null
    };

    return result;
  }, [currentStatus]);

  const handlePrevious = () => {
    if (canGoBack && previousStatus) {
      setProcessStatus(taskKey, productId, previousStatus, refreshKey);
    }
  };

  const handleNext = () => {
    if (canGoNext && nextStatus) {
      setProcessStatus(taskKey, productId, nextStatus, refreshKey);
    }
  };

  if (currentStatus === ITEM_DECLINED) {
    return (
      <div className={prefix}>
        <Button
          className={`${statusClassMap[ITEM_PENDING]} ${statusLabelClass[ITEM_PENDING]}`}
          leftIcon={<NubeskIcons icon="arrow-left" />}
          content={t("tasks:items.status.pending")}
          xs
          onClick={() => setProcessStatus(taskKey, productId, ITEM_PENDING, refreshKey)}
        />
      </div>
    );
  }

  return (
    <div className={prefix}>
      {canGoBack && previousStatus && (
        <Button
          className={`${statusClassMap[previousStatus]} ${statusLabelClass[previousStatus]}`}
          leftIcon={<NubeskIcons icon="arrowLeft" />}
          content={t(`tasks:items.status.${previousStatus}`)}
          xs
          onClick={handlePrevious}
        />
      )}
      {canGoNext && nextStatus && (
        <Button
          className={`${statusClassMap[nextStatus]} ${statusLabelClass[nextStatus]}`}
          content={t(`tasks:items.status.${nextStatus}`)}
          rightIcon={<NubeskIcons icon="arrowRight" />}
          xs
          onClick={handleNext}
        />
      )}
    </div>
  );
};

TaskItemStatusButtons.propTypes = {
  taskKey: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  currentStatus: PropTypes.oneOf([
    ITEM_PENDING,
    ITEM_INPROGRESS,
    ITEM_TOREVIEW,
    ITEM_DONE,
    ITEM_DECLINED
  ]).isRequired,
  refreshKey: PropTypes.string
};

export default TaskItemStatusButtons;
